import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type SystemItem = {
  id: string;
  name: string;
  code: string;
  app_base_path: string;
  order_index?: number;
};

export function useUserSystems() {
  return useQuery({
    queryKey: ['user-systems'],
    queryFn: async (): Promise<SystemItem[]> => {
      const { data, error } = await supabase.functions.invoke('get-user-systems');
      
      if (error) {
        console.error('Error fetching user systems:', error);
        throw new Error('Failed to fetch user systems');
      }
      
      const systems = data || [];
      console.log('🔍 [useUserSystems] Systems from edge function:', systems);
      return systems;
    },
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev ?? [],
  });
}

export function useDefaultSystem() {
  return useQuery({
    queryKey: ['default-system'],
    queryFn: async (): Promise<string | null> => {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('default_system_id')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();
      
      if (error) {
        console.error('Error fetching default system:', error);
        return null;
      }
      
      return profile?.default_system_id || null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateDefaultSystem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (systemId: string | null) => {
      const { data, error } = await supabase.functions.invoke('update-default-system', {
        body: { systemId }
      });
      
      if (error) {
        console.error('Error updating default system:', error);
        throw new Error('Failed to update default system');
      }
      
      return data;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['default-system'] });
      queryClient.invalidateQueries({ queryKey: ['entry-target'] });
      
      toast({
        title: "Sistema padrão atualizado",
        description: "Sua preferência foi salva com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useEntryTarget(userId?: string) {
  return useQuery({
    queryKey: ['entry-target', userId],
    queryFn: async (): Promise<{ basePath: string; systemId?: string; hasAccess?: boolean }> => {
      const { data, error } = await supabase.functions.invoke('get-entry-target');
      
      if (error) {
        console.error('Error getting entry target:', error);
        return { basePath: '/', hasAccess: false };
      }
      
      return data || { basePath: '/', hasAccess: false };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes caching to prevent excessive calls
    retry: false, // Don't retry on failure to prevent loops
  });
}